// src/pages/ModuleManager.js
import React, { useState, useEffect } from 'react';
import { Package, ToggleLeft, ToggleRight, Shield, Lock } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const ICON_COLORS = { 'layout-dashboard': '#4f8ef7', 'users-2': '#22c55e', 'user-cog': '#f59e0b', 'server': '#8b5cf6', 'bar-chart-3': '#06b6d4', 'calendar': '#ec4899', 'box': '#6b7280' };

export default function ModuleManager() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { hasRole, fetchMe } = useAuth();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/modules'); setModules(data); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (mod) => {
    if (mod.is_core) return toast.warning('Modulele de bază nu pot fi dezactivate');
    try {
      await api.put(`/modules/${mod.id}/toggle`);
      toast.success(`Modul "${mod.display_name}" ${mod.is_active ? 'dezactivat' : 'activat'}`);
      load();
      fetchMe(); // refresh sidebar
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  const core = modules.filter(m => m.is_core);
  const optional = modules.filter(m => !m.is_core);

  const ModuleCard = ({ mod }) => {
    const color = ICON_COLORS[mod.icon] || '#6b7280';
    return (
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Package size={22} color={color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{mod.display_name}</span>
            {mod.is_core && <span className="badge badge-info" style={{ fontSize: 10 }}><Shield size={10} /> Core</span>}
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>v{mod.version}</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{mod.description}</p>
          {mod.route && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>{mod.route}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: mod.is_active ? 'var(--success)' : 'var(--text-secondary)', fontWeight: 600 }}>
            {mod.is_active ? 'Activ' : 'Inactiv'}
          </span>
          <button onClick={() => toggle(mod)} style={{ background: 'none', border: 'none', cursor: mod.is_core ? 'not-allowed' : 'pointer', opacity: mod.is_core ? 0.5 : 1 }}>
            {mod.is_active
              ? <ToggleRight size={32} color="var(--success)" />
              : <ToggleLeft size={32} color="var(--text-secondary)" />}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Manager Module</div><div className="page-subtitle">Activează sau dezactivează module</div></div>
      </div>

      <div style={{ background: 'var(--warning)15', border: '1px solid var(--warning)33', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--warning)' }}>
        <Lock size={16} />
        Modificările se reflectă imediat în meniu. Modulele Core nu pot fi dezactivate.
      </div>

      {loading ? <div className="loading-center"><span className="spinner" /></div> : (
        <>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Module de Bază</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {core.map(m => <ModuleCard key={m.id} mod={m} />)}
          </div>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Module Opționale</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {optional.map(m => <ModuleCard key={m.id} mod={m} />)}
          </div>
        </>
      )}
    </div>
  );
}
